package com.dotdrives.controller

import com.dotdrives.model.FormPaymentReadiness
import com.dotdrives.core.DotController
import com.dotdrives.model.EmployerMatch
import com.dotdrives.model.parseFormGoal
import com.dotdrives.model.parseFormCloseAt
import com.dotdrives.model.validateFormLogo
import com.dotdrives.model.FormDesign
import com.dotdrives.model.FormField
import com.dotdrives.model.compatibleContactField
import com.dotdrives.model.safeGivingUrl
import com.dotdrives.model.FormAmount
import com.dotdrives.model.FormSubmission
import com.dotdrives.model.FormToggles
import com.dotdrives.model.GivingForm
import com.dotdrives.model.GivingFormStatus
import com.dotdrives.model.Target
import com.dotdrives.repository.FormRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch

enum class BuilderTab {
    Design,
    Settings,
    Receipts,
}

data class FormBuilderUiState(
    val loading: Boolean = false,
    val form: GivingForm? = null,
    val targets: List<Target> = emptyList(),
    val activeTab: BuilderTab = BuilderTab.Design,
    val draftStory: String = "",
    val draftEmployerMatches: List<EmployerMatch> = emptyList(),
    val draftDesign: FormDesign = FormDesign(),
    val draftFields: List<FormField> = emptyList(),
    val draftAmounts: List<FormAmount> = emptyList(),
    val draftToggles: FormToggles = FormToggles(),
    val draftTargetId: String? = null,
    val draftName: String = "",
    val draftHeadline: String = "",
    val draftGoalAmount: String = "",
    val draftCloseAt: String = "",
    val uploadingLogo: Boolean = false,
    val draftShowProgressBar: Boolean = false,
    val draftReceiptSubject: String = "",
    val draftReceiptMessage: String = "",
    val draftIncludeIrsLanguage: Boolean = true,
    /**
     * null = either not loaded yet, or the raw `form_submissions` query for this one form
     * returned zero rows -- ambiguous between "genuinely no gifts yet" and "this role's rows
     * are hidden by `has_perm_strict`" (see [FormRepository.submissionFeed]). The view must not
     * render "No gifts yet" here; that would assert a fact this state does not know.
     */
    val submissions: List<FormSubmission>? = null,
    val stripeConnected: Boolean? = null,
    val paymentReadiness: FormPaymentReadiness? = null,
    /** True only when the Stripe-connection check itself failed (network/exception) -- distinct from [stripeConnected] being unknown because the form hasn't loaded yet. */
    val stripeCheckFailed: Boolean = false,
    val saving: Boolean = false,
    val error: String? = null,
) {
    val dirty: Boolean
        get() = form != null &&
            (
                draftStory != form.config.story ||
                    draftEmployerMatches != form.config.employerMatches ||
                    draftDesign != form.config.design ||
                    draftFields != form.config.fields ||
                    draftAmounts != form.config.amounts ||
                    draftToggles != form.config.toggles ||
                    draftTargetId != form.targetId ||
                    draftName != form.name ||
                    draftHeadline != form.headline ||
                    parseFormGoal(draftGoalAmount).isFailure ||
                    parseFormGoal(draftGoalAmount).getOrNull() != form.goalAmount ||
                    parseFormCloseAt(draftCloseAt).isFailure ||
                    parseFormCloseAt(draftCloseAt).getOrNull() != form.closeAt ||
                    draftShowProgressBar != form.showProgressBar ||
                    draftReceiptSubject != form.config.receipt.subject ||
                    draftReceiptMessage != form.config.receipt.message ||
                    draftIncludeIrsLanguage != form.config.receipt.includeIrsLanguage
                )
}

class FormBuilderController(
    private val repo: FormRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<FormBuilderUiState>(FormBuilderUiState(), scope) {
    private var generation = 0L
    private var loadedFormId: String? = null

    fun load(formId: String) {
        loadedFormId = formId
        val gen = nextGeneration()
        update { FormBuilderUiState(loading = true) }
        scope.launch {
            try {
                val results = coroutineScope {
                    val form = async { repo.getForm(orgId, formId) }
                    val targets = async { repo.targets(orgId) }
                    val submissions = async { repo.submissionFeed(orgId, formId, SUBMISSION_FEED_LIMIT) }
                    val stripe = async { repo.paymentReadiness(orgId) }
                    BuilderLoadResults(form.await(), targets.await(), submissions.await(), stripe.await())
                }
                val formResult = results.form
                val targetsResult = results.targets
                val submissionsResult = results.submissions
                val stripeResult = results.stripe
                if (!isCurrent(gen)) return@launch
                formResult
                    .onSuccess { form ->
                        // Ancillary failures (targets/submissions/Stripe) must not be silently
                        // swallowed into an empty/false value that reads as real data: a failed
                        // targets load must not look like "this org truly has no targets," and a
                        // failed Stripe check must not be read by publish() as "not connected."
                        // The form itself still loads and the builder stays usable; the failure
                        // surfaces as an error banner instead of being dropped.
                        val ancillaryError = targetsResult.exceptionOrNull()
                            ?: submissionsResult.exceptionOrNull()
                            ?: stripeResult.exceptionOrNull()
                        update {
                            if (isCurrent(gen)) {
                                it.copy(
                                    loading = false,
                                    form = form,
                                    targets = targetsResult.getOrNull().orEmpty(),
                                    draftStory = form.config.story,
                                    draftEmployerMatches = form.config.employerMatches,
                                    draftDesign = form.config.design,
                                    draftFields = form.config.fields,
                                    draftAmounts = form.config.amounts,
                                    draftToggles = form.config.toggles,
                                    draftTargetId = form.targetId,
                                    draftName = form.name,
                                    draftHeadline = form.headline,
                                    draftGoalAmount = form.goalAmount?.let(::formatGoalInput).orEmpty(),
                                    draftCloseAt = form.closeAt?.toString()?.removeSuffix("Z").orEmpty(),
                                    draftShowProgressBar = form.showProgressBar,
                                    draftReceiptSubject = form.config.receipt.subject,
                                    draftReceiptMessage = form.config.receipt.message,
                                    draftIncludeIrsLanguage = form.config.receipt.includeIrsLanguage,
                                    submissions = submissionsResult.getOrNull(),
                                    stripeConnected = stripeResult.getOrNull()?.connectionPreserved,
                                    paymentReadiness = stripeResult.getOrNull(),
                                    stripeCheckFailed = stripeResult.isFailure,
                                    error = ancillaryError?.let { it.message ?: "Something went wrong" },
                                )
                            } else {
                                it
                            }
                        }
                    }
                    .onFailure { publishError(it, gen) }
            } catch (error: Exception) {
                publishError(error, gen)
            }
        }
    }

    fun retry() {
        loadedFormId?.let(::load)
    }

    fun setActiveTab(tab: BuilderTab) = update { it.copy(activeTab = tab) }

    fun setStory(value: String) = update { it.copy(draftStory = value, error = null) }

    fun setEmployerMatches(value: List<EmployerMatch>) = update { it.copy(draftEmployerMatches = value, error = null) }

    fun setDesign(value: FormDesign) = update { it.copy(draftDesign = value, error = null) }

    fun setFields(value: List<FormField>) = update { it.copy(draftFields = value, error = null) }

    fun setAmounts(amounts: List<FormAmount>) {
        val default = amounts.indexOfFirst(FormAmount::isDefault).takeIf { it >= 0 } ?: 0
        update { it.copy(draftAmounts = amounts.mapIndexed { index, amount -> amount.copy(isDefault = index == default) }, error = null) }
    }

    fun moveAmount(from: Int, to: Int) {
        val items = state.value.draftAmounts.toMutableList()
        if (from !in items.indices || to !in items.indices || from == to) return
        items.add(to, items.removeAt(from))
        setAmounts(items)
    }

    fun moveField(from: Int, to: Int) {
        val items = state.value.draftFields.toMutableList()
        if (from !in items.indices || to !in items.indices || from == to) return
        items.add(to, items.removeAt(from))
        setFields(items.mapIndexed { index, field -> field.copy(order = index) })
    }

    fun uploadLogo(name: String, mime: String, bytes: ByteArray) {
        val current = state.value
        val form = current.form ?: return
        if (current.uploadingLogo || current.saving) return
        val gen = generation
        update { it.copy(uploadingLogo = true, error = null) }
        scope.launch {
            try {
                validateFormLogo(name, mime, bytes)
                val uploaded = repo.uploadLogo(orgId, form.id, name, mime, bytes).getOrThrow()
                if (!isCurrent(gen)) return@launch
                update { it.copy(uploadingLogo = false, draftDesign = it.draftDesign.copy(
                    logoUrl = uploaded.url, logoStoragePath = null, logoFileName = uploaded.fileName,
                    logoMimeType = uploaded.mimeType, logoSizeBytes = uploaded.sizeBytes), error = null) }
            } catch (error: Exception) {
                if (error is CancellationException) throw error
                if (isCurrent(gen)) update { it.copy(uploadingLogo = false, error = error.message ?: "Logo upload failed. Try again.") }
            }
        }
    }

    fun setToggles(toggles: FormToggles) = update { it.copy(draftToggles = toggles, error = null) }

    fun setTargetId(id: String?) = update { it.copy(draftTargetId = id, error = null) }

    fun setName(name: String) = update { it.copy(draftName = name, error = null) }

    fun setHeadline(headline: String) = update { it.copy(draftHeadline = headline, error = null) }

    fun setGoalAmount(text: String) = update { it.copy(draftGoalAmount = text, error = null) }

    fun setCloseAt(text: String) = update { it.copy(draftCloseAt = text, error = null) }

    fun setShowProgressBar(value: Boolean) = update { it.copy(draftShowProgressBar = value, error = null) }

    fun setReceiptSubject(text: String) = update { it.copy(draftReceiptSubject = text, error = null) }

    fun setReceiptMessage(text: String) = update { it.copy(draftReceiptMessage = text, error = null) }

    fun setIncludeIrsLanguage(value: Boolean) = update { it.copy(draftIncludeIrsLanguage = value, error = null) }

    /**
     * Persists the Design-tab draft (amounts/toggles/target/receipt) and the Settings-tab draft
     * in one round trip. `config` is a single JSONB document (docs/schema.md), so any save that
     * touches amounts/toggles/receipt must resend the whole object -- there is no partial-key
     * update. The builder's topbar "Save changes" and the Receipts tab's "Save receipts" both
     * call this: the receipt fields live inside that same document, so a receipts-only save is
     * this same action under a different button.
     */
    fun save() {
        if (state.value.saving || state.value.uploadingLogo) return
        update { it.copy(saving = true, error = null) }
        scope.launch {
            performSave()
                .onSuccess { saved -> update { it.copy(saving = false, form = saved, error = null) } }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                }
        }
    }

    fun saveReceipts() = save()

    /**
     * Moves `status` draft -> live -- the `giving_forms_update` policy exists specifically for
     * this. Saves the current draft first *only if it is dirty*: every new form starts as a
     * draft, so "create, add amounts, publish" is the primary flow and publishing without saving
     * first would discard everything the org just designed -- but `saveConfig` bumps `version`
     * unconditionally on every call, and `form_submissions.form_version` is what a donor's
     * submission is validated against, so publishing an already-saved, non-dirty draft must not
     * bump it for no reason.
     */
    fun publish() {
        val current = state.value
        if (current.form == null || current.saving || current.uploadingLogo) return
        if (current.stripeCheckFailed) {
            update { it.copy(error = "Couldn't verify your Stripe connection — try again.") }
            return
        }
        if (current.paymentReadiness?.canPublish != true) {
            update { it.copy(error = current.paymentReadiness?.message ?: "Checking payment readiness. Try again shortly.") }
            return
        }
        update { it.copy(saving = true, error = null) }
        scope.launch {
            val savedForm = if (current.dirty) {
                performSave().getOrElse { error ->
                    if (error is CancellationException) throw error
                    update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                    return@launch
                }
            } else {
                current.form
            }
            repo.setStatus(orgId, savedForm.id, GivingFormStatus.LIVE)
                .onSuccess { updated -> update { it.copy(saving = false, form = updated, error = null) } }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    // The config/settings save above did succeed even though the status flip
                    // failed -- reflect that saved form rather than the pre-save one, so a retry
                    // of "Publish form" doesn't resend already-persisted edits as if they were lost.
                    update { it.copy(saving = false, form = savedForm, error = error.message ?: "Something went wrong") }
                }
        }
    }

    fun archive() = setStatus(GivingFormStatus.ARCHIVED)

    private suspend fun performSave(): Result<GivingForm> {
        val current = state.value
        val form = current.form ?: return Result.failure(IllegalStateException("No form loaded"))
        val goal = parseFormGoal(current.draftGoalAmount).getOrElse { return Result.failure(it) }
        val closeAt = parseFormCloseAt(current.draftCloseAt).getOrElse { return Result.failure(it) }
        if (current.draftFields.any { it.label != null && it.label.length > 120 })
            return Result.failure(IllegalArgumentException("Keep donor field labels within 120 characters."))
        if (current.draftAmounts.any { !it.amount.isFinite() || it.amount < 0.50 || it.amount > 1_000_000 || kotlin.math.abs(it.amount * 100 - kotlin.math.round(it.amount * 100)) > 0.0001 })
            return Result.failure(IllegalArgumentException("Suggested gifts must be between $0.50 and $1,000,000, with at most two decimal places."))
        if (current.draftStory.length > 5000)
            return Result.failure(
                IllegalArgumentException("Keep the campaign story within 5,000 characters.")
            )
        if (
            current.draftEmployerMatches.size > 30 ||
                current.draftEmployerMatches.any {
                    it.employer.isBlank() ||
                        it.employer.length > 120 ||
                        it.instructions.isBlank() ||
                        it.instructions.length > 2000 ||
                        (it.domain.isNotBlank() &&
                            !Regex("[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}").matches(it.domain)) ||
                        (it.url != null && !safeGivingUrl(it.url))
                }
        )
            return Result.failure(
                IllegalArgumentException(
                    "Each employer needs a name and real instructions. Use a valid domain and an HTTPS instructions link."
                )
            )
        if (
            current.draftDesign.backgroundColor?.let { !Regex("#[0-9a-fA-F]{6}").matches(it) } ==
                true ||
                current.draftDesign.logoUrl?.let {
                    !safeGivingUrl(it) && !validStoredLogoPath(orgId, current.draftDesign.logoStoragePath)
                } == true
        ) {
            return Result.failure(
                IllegalArgumentException("Use a six-digit hex brand color and an HTTPS logo URL.")
            )
        }
        if (current.draftFields.any { field ->
                field.type == "select" && (field.options.isNullOrEmpty() ||
                    field.options.any { it.isBlank() } ||
                    field.options.map { it.trim() }.distinct().size != field.options.size)
            }) return Result.failure(IllegalArgumentException("Each dropdown needs nonblank, unique choices."))
        if (current.draftFields.any { !compatibleContactField(it) }) {
            return Result.failure(
                IllegalArgumentException(
                    "Reserved contact fields need compatible types: email uses Email or Short text; name fields use Short text, without options."
                )
            )
        }
        if (
            current.draftFields.size > 30 ||
                current.draftFields.map { it.key }.distinct().size != current.draftFields.size ||
                current.draftFields.any {
                    !Regex("[a-z][a-z0-9_]{0,49}").matches(it.key) ||
                        it.type !in
                            listOf(
                                "text",
                                "email",
                                "tel",
                                "number",
                                "textarea",
                                "checkbox",
                                "select",
                            ) ||
                        (it.type == "select" && it.options.isNullOrEmpty())
                }
        )
            return Result.failure(
                IllegalArgumentException(
                    "Use unique field names and add options for each choice field."
                )
            )
        val config =
            form.config.copy(
                story = current.draftStory,
                employerMatches = current.draftEmployerMatches,
                design = current.draftDesign,
                fields = current.draftFields,
                amounts = current.draftAmounts,
                toggles = current.draftToggles,
                receipt =
                    form.config.receipt.copy(
                        subject = current.draftReceiptSubject,
                        message = current.draftReceiptMessage,
                        includeIrsLanguage = current.draftIncludeIrsLanguage,
                    ),
            )
        val configResult = repo.saveConfig(orgId, form.id, config, current.draftTargetId)
        if (configResult.isFailure) return configResult
        return repo.saveSettings(
            orgId = orgId,
            formId = form.id,
            name = current.draftName,
            headline = current.draftHeadline,
            goalAmount = goal,
            showProgressBar = current.draftShowProgressBar,
            closeAt = closeAt,
        )
    }

    private fun validStoredLogoPath(orgId: String, path: String?): Boolean {
        val prefix = "$orgId/form-design/"
        val remainder = path?.takeIf { it.startsWith(prefix) && !it.contains("..") }
            ?.removePrefix(prefix)
            ?: return false
        val parts = remainder.split('/')
        return parts.size == 2 && parts.all(String::isNotBlank)
    }

    private fun setStatus(status: String) {
        val form = state.value.form ?: return
        update { it.copy(saving = true, error = null) }
        scope.launch {
            repo.setStatus(orgId, form.id, status)
                .onSuccess { updated -> update { it.copy(saving = false, form = updated, error = null) } }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun publishError(error: Throwable, gen: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(gen)) return
        update {
            if (isCurrent(gen)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it
        }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(gen: Long): Boolean = gen == generation
}

private fun formatGoalInput(amount: Double): String =
    if (amount == amount.toLong().toDouble()) amount.toLong().toString() else amount.toString()

private const val SUBMISSION_FEED_LIMIT = 30

private data class BuilderLoadResults(
    val form: Result<GivingForm>,
    val targets: Result<List<Target>>,
    val submissions: Result<List<FormSubmission>?>,
    val stripe: Result<FormPaymentReadiness>,
)
